// src/components/MainNavbar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DOCS_URL } from '../constants/docsLinks';
import './MainNavbar.css';

/* ── Inline logo SVG (replace the <svg>…</svg> block with yours) ── */
const Logo = () => (
    <svg
        width="32"
        height="32"
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <rect x="10" y="10" width="80" height="80" rx="5" stroke="currentColor" stroke-width="2" fill="none"/>

        <circle cx="25" cy="25" r="8" fill="#00cca3ff"/>
        <circle cx="50" cy="25" r="8" fill="#0dc6afff"/>
        <circle cx="75" cy="25" r="8" fill="#19b9baff"/>

        <circle cx="25" cy="50" r="8" fill="#25abc5ff"/>
        <circle cx="50" cy="50" r="8" fill="#2ba1cbff"/>
        <circle cx="75" cy="50" r="8" fill="#3196d0ff"/>

        <circle cx="25" cy="75" r="8" fill="#00cca3ff"/>
        <circle cx="50" cy="75" r="8" fill="#0dc6afff"/>
        <circle cx="75" cy="75" r="8" fill="#19b9baff"/>
    </svg>
);

/* ── Documentation link: a separate site, so a plain <a> in a new tab ── */
const DocsLink = ({ className, onClick }) => (
    <a
        href={DOCS_URL}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
    >
        Docs
    </a>
);

/* ── Avatar initials helper ── */
const getInitials = (user) => {
    if (!user) return '?';
    if (user.first_name && user.last_name)
        return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    if (user.first_name) return user.first_name[0].toUpperCase();
    if (user.email) return user.email[0].toUpperCase();
    return '?';
};

const NAV_LINKS = [
    {to: '/photo-processing', label: 'Detection'},
    {to: '/jobs', label: 'Photo'},
    {to: '/experiments', label: 'Experiment'},
    {to: '/groups', label: 'Groups'},
];

const MainNavbar = () => {
    const {user, logout, isAuthenticated} = useAuth();
    const navigate = useNavigate();

    const [menuOpen, setMenuOpen] = useState(false);
    const [dropOpen, setDropOpen] = useState(false);
    const [scrolled,    setScrolled]    = useState(false);
  const dropRef = useRef(null);

  /* shadow on scroll */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* close avatar dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    setDropOpen(false);
    logout();
  };

  const handleProfile = () => {
    setDropOpen(false);
    navigate('/profile');
  };

  return (
    <header className={`mnav${scrolled ? ' mnav--scrolled' : ''}`}>
      <div className="mnav__inner">

        {/* ── Brand ── */}
        <Link to="/" className="mnav__brand" onClick={() => setMenuOpen(false)}>
          <Logo />
          <span className="mnav__brand-text">Chrometrica</span>
        </Link>

        {/* ── Desktop nav links ── */}
        {isAuthenticated && (
          <nav className="mnav__links" aria-label="Main navigation">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `mnav__link${isActive ? ' mnav__link--active' : ''}`
                }
              >
                {label}
              </NavLink>
            ))}
            <DocsLink className="mnav__link" />
          </nav>
        )}

        {/* ── Right side ── */}
        <div className="mnav__right">
          {isAuthenticated ? (
            <div className="mnav__avatar-wrap" ref={dropRef}>
              <button
                className="mnav__avatar-btn"
                onClick={() => setDropOpen((o) => !o)}
                aria-expanded={dropOpen}
                aria-label="User menu"
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={getInitials(user)}
                    className="mnav__avatar-img"
                  />
                ) : (
                  <span className="mnav__avatar-initials">
                    {getInitials(user)}
                  </span>
                )}
                <span className="mnav__avatar-name">
                  {user?.first_name || user?.email?.split('@')[0]}
                </span>
                <svg
                  className={`mnav__avatar-caret${dropOpen ? ' mnav__avatar-caret--open' : ''}`}
                  width="12" height="12" viewBox="0 0 12 12" fill="none"
                >
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8"
                        strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {dropOpen && (
                <div className="mnav__dropdown" role="menu">
                  <div className="mnav__dropdown-header">
                    <span className="mnav__dropdown-name">
                      {user?.first_name
                        ? `${user.first_name} ${user.last_name ?? ''}`.trim()
                        : user?.email}
                    </span>
                    <span className="mnav__dropdown-email">{user?.email}</span>
                  </div>
                  <div className="mnav__dropdown-divider" />
                  <button
                    className="mnav__dropdown-item"
                    role="menuitem"
                    onClick={handleProfile}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    My Profile
                  </button>
                  <button
                    className="mnav__dropdown-item mnav__dropdown-item--danger"
                    role="menuitem"
                    onClick={handleLogout}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <DocsLink className="mnav__link" />
              <Link to="/login" className="mnav__login-btn">Sign in</Link>
            </>
          )}

          {/* ── Mobile hamburger ── */}
          {isAuthenticated && (
            <button
              className={`mnav__burger${menuOpen ? ' mnav__burger--open' : ''}`}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
            >
              <span /><span /><span />
            </button>
          )}
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      {isAuthenticated && (
        <div className={`mnav__drawer${menuOpen ? ' mnav__drawer--open' : ''}`}>
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `mnav__drawer-link${isActive ? ' mnav__drawer-link--active' : ''}`
              }
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </NavLink>
          ))}
          <DocsLink className="mnav__drawer-link" onClick={() => setMenuOpen(false)} />
          <div className="mnav__drawer-divider" />
          <button className="mnav__drawer-link" onClick={() => { setMenuOpen(false); navigate('/profile'); }}>
            My Profile
          </button>
          <button className="mnav__drawer-link mnav__drawer-link--danger" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      )}
    </header>
  );
};

export default MainNavbar;
